// FE-SCRM-056 — Playbook card on Opportunity detail.
//
// Two states, gated by ``opportunity.playbook_applied_id``:
//
//   null / undefined → empty state + "Apply playbook" primary button.
//   set              → applied state: playbook name + step count +
//                       "Re-apply" affordance (confirmation modal
//                       calls out the BE's idempotency rule).
//
// Step count comes from the playbook detail query — the BE serializes
// ``activity_blocks[]`` on detail GET so we don't have to keep an
// FE-side seed list in sync with the BE. ``applied_at`` /
// ``applied_by`` are not exposed on Opportunity detail in v1; the
// brief flags Phase 7b is allowed to lean on the playbook detail
// alone for the applied-state copy.

import { useState } from "react";

import { Button, Card, Modal, Toast } from "@/design-system";

import { ApplyPlaybookModal } from "./ApplyPlaybookModal";
import {
  useApplyPlaybookMutation,
  usePlaybookDetailQuery,
} from "./hooks";
import type { ApplyPlaybookResponse } from "./types";


interface Props {
  opportunity: {
    opportunity_id: string;
    renewal_year: number | null;
    renewal_month: number | null;
    playbook_applied_id: string | null;
  };
}


export function PlaybookCard({ opportunity }: Props) {
  const [applyOpen, setApplyOpen] = useState(false);
  const [reapplyConfirmOpen, setReapplyConfirmOpen] = useState(false);
  const [toast, setToast] = useState<
    | null
    | { variant: "success" | "error"; title: string; description?: string }
  >(null);

  const applied = Boolean(opportunity.playbook_applied_id);
  const detail = usePlaybookDetailQuery(opportunity.playbook_applied_id);
  const apply = useApplyPlaybookMutation(opportunity.opportunity_id);

  function onApplySuccess(res: ApplyPlaybookResponse) {
    const count = res.spawned_activities.length;
    setToast({
      variant: "success",
      title: count > 0
        ? `Playbook applied — ${count} activit${count === 1 ? "y" : "ies"} spawned.`
        : "Playbook already applied; no new activities spawned.",
    });
  }

  function confirmReapply() {
    if (!opportunity.playbook_applied_id) return;
    apply.mutate(
      { playbook_id: opportunity.playbook_applied_id },
      {
        onSuccess: (res) => {
          setReapplyConfirmOpen(false);
          // Idempotency: a BE-side existing application returns the
          // same row with zero net new spawns. Tell the user.
          const count = res.spawned_activities.length;
          setToast({
            variant: "success",
            title: count > 0
              ? `Playbook re-applied — ${count} activities spawned.`
              : "Playbook already applied; no new activities spawned.",
          });
        },
      },
    );
  }

  return (
    <>
      <Card
        data-testid="playbook-card"
        className="flex flex-col gap-3 p-4"
      >
        <header className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary">
            Playbook
          </h2>
        </header>

        {applied ? (
          <AppliedState
            playbookName={detail.data?.name ?? "Applied playbook"}
            stepCount={detail.data?.activity_blocks.length ?? null}
            onReapply={() => setReapplyConfirmOpen(true)}
          />
        ) : (
          <EmptyState onApply={() => setApplyOpen(true)} />
        )}
      </Card>

      {applyOpen ? (
        <ApplyPlaybookModal
          opportunity={opportunity}
          onClose={() => setApplyOpen(false)}
          onSuccess={onApplySuccess}
        />
      ) : null}

      {reapplyConfirmOpen ? (
        <ReapplyConfirmModal
          isPending={apply.isPending}
          onClose={() => setReapplyConfirmOpen(false)}
          onConfirm={confirmReapply}
        />
      ) : null}

      {toast ? (
        <Toast
          variant={toast.variant}
          title={toast.title}
          description={toast.description}
          onDismiss={() => setToast(null)}
        />
      ) : null}
    </>
  );
}


function EmptyState({ onApply }: { onApply: () => void }) {
  return (
    <div
      data-testid="playbook-card-empty"
      className="flex flex-col gap-3"
    >
      <p className="text-xs text-text-secondary">
        No playbook applied yet. Apply a sales playbook to spawn the
        cadence of activities tied to this opportunity's renewal.
      </p>
      <div>
        <Button
          variant="primary"
          onClick={onApply}
          data-testid="playbook-apply-button"
        >
          Apply playbook
        </Button>
      </div>
    </div>
  );
}


function AppliedState({
  playbookName, stepCount, onReapply,
}: {
  playbookName: string;
  stepCount: number | null;
  onReapply: () => void;
}) {
  return (
    <div
      data-testid="playbook-card-applied"
      className="flex items-center justify-between gap-3"
    >
      <div className="flex flex-col">
        <span className="text-sm font-semibold text-text-primary">
          {playbookName}
        </span>
        <span className="text-xs text-text-secondary">
          {stepCount !== null
            ? `${stepCount} step${stepCount === 1 ? "" : "s"} • see the Activity timeline below`
            : "see the Activity timeline below"}
        </span>
      </div>
      <Button
        variant="secondary"
        onClick={onReapply}
        data-testid="playbook-reapply-button"
      >
        Re-apply
      </Button>
    </div>
  );
}


function ReapplyConfirmModal({
  isPending, onClose, onConfirm,
}: {
  isPending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title="Re-apply playbook?"
      size="sm"
    >
      <div data-testid="reapply-confirm-modal" className="flex flex-col gap-3">
        <p className="text-sm text-text-secondary">
          Re-applying is idempotent: because this opportunity already
          has this playbook applied, no new activities will be
          spawned. Existing activities stay in place.
        </p>
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? "Re-applying…" : "Re-apply"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
